import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getClientDeletionPreview,
  deleteClientAccount,
  zeroClientWallet,
} from "./adminApi";

const REASON_MIN = 10;
const REASON_MAX = 500;

const LABELS = {
  // anonimizzati / chiusi
  prenotazioni_indirizzo: "Indirizzi negli ordini",
  checkout_sessions_booking: "Indirizzi nei checkout pagati",
  subscription_checkout_sessions_pickup_address: "Indirizzi nei checkout abbonamento",
  user_subscriptions_pickup_address: "Indirizzi negli abbonamenti",
  user_subscriptions_close: "Abbonamenti attivi chiusi",
  reviews_comment: "Commenti delle recensioni",
  email_log: "Log email",
  promo_codes_owner: "Codici promo scollegati",
  fornitori_user: "Fornitore scollegato",
  // cancellati
  checkout_sessions_unpaid: "Checkout non pagati",
  subscription_checkout_sessions_unpaid: "Checkout abbonamento non pagati",
  referrals: "Referral",
  referral_codes: "Codici referral",
  users_address: "Indirizzi salvati",
  password_resets: "Reset password",
  email_verification_tokens: "Token verifica email",
  user_push_tokens: "Token notifiche push",
  promo_customers: "Promo assegnate",
  organization_members: "Appartenenza a organizzazioni",
  supplier_profile_change_requests: "Richieste profilo fornitore",
  fornitori_pending: "Richieste fornitore",
  data_export_requests: "Richieste export dati",
  push_notification_log: "Log notifiche push",
  // conservati
  prenotazioni: "Ordini",
  checkout_sessions_paid: "Checkout pagati",
  subscription_checkout_sessions_paid: "Checkout abbonamento pagati",
  user_subscriptions: "Abbonamenti",
  wallet_transactions: "Movimenti wallet",
  wallet_accounts: "Conto wallet",
  promo_redemptions: "Utilizzi promo",
  reviews: "Recensioni (voto)",
  account_deletion_requests: "Richieste di cancellazione",
};

const euro = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

function CountList({ title, counts }) {
  const entries = Object.entries(counts || {}).filter(([, n]) => Number(n) > 0);
  return (
    <div className="ac-del-col">
      <div className="ac-del-col-title">{title}</div>
      {entries.length === 0 ? (
        <div className="ac-del-muted">Nessun dato</div>
      ) : (
        <ul className="ac-del-list">
          {entries.map(([key, n]) => (
            <li key={key}>
              <span>{LABELS[key] || key}</span>
              <strong>{n}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DeleteClientModal({ token, client, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const [notifyUser, setNotifyUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [walletOpen, setWalletOpen] = useState(false);
  const [walletReason, setWalletReason] = useState("");
  const [zeroing, setZeroing] = useState(false);

  const loadPreview = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      setPreview(await getClientDeletionPreview(token, client.id));
    } catch (e) {
      setLoadError(e.message || "Errore anteprima cancellazione");
    } finally {
      setLoading(false);
    }
  }, [token, client.id]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const reasonLen = reason.trim().length;
  const reasonOk = reasonLen >= REASON_MIN && reasonLen <= REASON_MAX;
  const emailOk =
    !!confirmEmail.trim() &&
    confirmEmail.trim().toLowerCase() === String(client.email || "").trim().toLowerCase();
  const canSubmit = !!preview?.deletable && emailOk && reasonOk && !submitting;

  const walletReasonLen = walletReason.trim().length;
  const walletReasonOk = walletReasonLen >= REASON_MIN && walletReasonLen <= REASON_MAX;

  async function handleZeroWallet() {
    if (!walletReasonOk || zeroing) return;
    try {
      setZeroing(true);
      const out = await zeroClientWallet(token, client.id, walletReason.trim());
      toast.success(`Saldo azzerato (${euro(out.previous_balance_cents)})`);
      setWalletOpen(false);
      setWalletReason("");
      await loadPreview();
    } catch (e) {
      toast.error(e.message || "Errore azzeramento saldo");
    } finally {
      setZeroing(false);
    }
  }

  async function handleDelete() {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      const out = await deleteClientAccount(token, client.id, {
        confirmEmail: confirmEmail.trim(),
        reason: reason.trim(),
        notifyUser,
      });
      toast.success("Cliente eliminato");
      if (out.stripe_to_complete) {
        toast.error(
          "Stripe non completato: verifica l'elenco \"Cancellazioni con Stripe da completare\"",
          { duration: 10000 }
        );
      }
      onDeleted?.(out);
    } catch (e) {
      toast.error(e.message || "Errore cancellazione cliente");
      if (e.blockers) await loadPreview();
    } finally {
      setSubmitting(false);
    }
  }

  const blockers = preview?.blockers || [];
  const counts = preview?.counts || {};

  return (
    <div className="ac-overlay" role="dialog" aria-modal="true" aria-labelledby="ac-del-title">
      <div className="ac-modal ac-modal--narrow">
        <div className="ac-modal-h">
          <div className="ac-title" id="ac-del-title">
            Elimina cliente #{client.id}
          </div>
          <button className="ac-close" onClick={onClose} aria-label="Chiudi">
            ✕
          </button>
        </div>

        <div className="ac-modal-body ac-del-body">
          <p className="ac-del-intro">
            I dati personali di <strong>{client.email}</strong> vengono anonimizzati o cancellati.
            Ordini, pagamenti, wallet e abbonamenti restano per la contabilità. L'operazione non è
            reversibile.
          </p>

          {loading && <div className="ac-del-muted">Caricamento anteprima…</div>}
          {loadError && <div className="ac-del-alert">{loadError}</div>}

          {preview && (
            <>
              {blockers.length > 0 && (
                <div className="ac-del-alert">
                  <div className="ac-del-alert-title">Cancellazione bloccata</div>
                  <ul>
                    {blockers.map((b) => (
                      <li key={b.code}>
                        {b.message}
                        {b.code === "WALLET_BALANCE" && (
                          <div className="ac-del-wallet">
                            <span>
                              Saldo attuale: <strong>{euro(b.balance_cents)}</strong>. Eventuali
                              rimborsi Stripe vanno fatti a mano prima.
                            </span>
                            {!walletOpen ? (
                              <button
                                type="button"
                                className="ac-btn"
                                onClick={() => setWalletOpen(true)}
                              >
                                Azzera saldo wallet…
                              </button>
                            ) : (
                              <div className="ac-del-wallet-form">
                                <label className="ac-del-label" htmlFor="ac-wallet-reason">
                                  Motivo dell'azzeramento (non inserire dati personali)
                                </label>
                                <textarea
                                  id="ac-wallet-reason"
                                  className="ac-del-textarea"
                                  rows={2}
                                  maxLength={REASON_MAX}
                                  value={walletReason}
                                  onChange={(e) => setWalletReason(e.target.value)}
                                />
                                <div className="ac-del-row">
                                  <span className="ac-del-muted">
                                    {walletReasonLen}/{REASON_MAX} (min {REASON_MIN})
                                  </span>
                                  <button
                                    type="button"
                                    className="ac-btn"
                                    onClick={() => setWalletOpen(false)}
                                  >
                                    Annulla
                                  </button>
                                  <button
                                    type="button"
                                    className="ac-btn ac-btn--danger"
                                    disabled={!walletReasonOk || zeroing}
                                    onClick={handleZeroWallet}
                                  >
                                    {zeroing ? "Azzeramento…" : "Conferma azzeramento"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="ac-del-grid">
                <CountList title="Conservati (contabilità)" counts={counts.conserve} />
                <CountList
                  title="Anonimizzati / chiusi"
                  counts={{ ...(counts.anonymize || {}), ...(counts.close || {}) }}
                />
                <CountList title="Cancellati" counts={counts.delete} />
              </div>

              {(preview.stripe?.subscriptions > 0 || preview.stripe?.customers > 0) && (
                <div className="ac-del-muted">
                  Stripe, dopo la conferma: {preview.stripe.subscriptions} abbonamenti da
                  cancellare, {preview.stripe.customers} customer da eliminare.
                </div>
              )}

              {preview.deletable && (
                <div className="ac-del-form">
                  <label className="ac-del-label" htmlFor="ac-del-email">
                    Riscrivi l'email del cliente per confermare
                  </label>
                  <input
                    id="ac-del-email"
                    className="ac-input"
                    autoComplete="off"
                    placeholder={client.email}
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                  />

                  <label className="ac-del-label" htmlFor="ac-del-reason">
                    Motivo (obbligatorio, non inserire dati personali)
                  </label>
                  <textarea
                    id="ac-del-reason"
                    className="ac-del-textarea"
                    rows={3}
                    maxLength={REASON_MAX}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="ac-del-muted">
                    {reasonLen}/{REASON_MAX} (min {REASON_MIN})
                  </div>

                  <label className="ac-check">
                    <input
                      type="checkbox"
                      checked={notifyUser}
                      onChange={(e) => setNotifyUser(e.target.checked)}
                    />
                    Invia al cliente un'email di conferma della cancellazione
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        <div className="ac-modal-f">
          <button className="ac-btn" onClick={onClose} disabled={submitting}>
            Annulla
          </button>
          <button
            className="ac-btn ac-btn--danger"
            onClick={handleDelete}
            disabled={!canSubmit}
          >
            {submitting ? "Eliminazione…" : "Elimina definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}
